import React, { useEffect, useState } from 'react';
import { db } from './firebaseConfig';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { useParams, useNavigate } from 'react-router-dom';
import { omitUndefinedDeep } from './firestoreSanitize';

const EditarQuestionario = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [questionario, setQuestionario] = useState(null);

  useEffect(() => {
    const fetchQuestionario = async () => {
      const docRef = doc(db, 'questionarios', id);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const data = docSnap.data(); // Obtém os dados do Firestore
        
        if (data.questions && Array.isArray(data.questions)) {
          setQuestionario({ ...data, gravarAudio: true });
        } else {
          alert('O documento não contém questions');
          console.warn("O documento não contém 'questions'.");
        }
      } else {
        console.log("O documento não foi encontrado!");
      }
    };

    fetchQuestionario();
  }, [id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setQuestionario((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleQuestionChange = (qId, field, value) => {
    setQuestionario((prev) => ({
      ...prev,
      questions: prev.questions.map((q, index) =>
        index === qId ? { ...q, [field]: value } : q
      ),
    }));
  };

  const handleOptionChange = (qId, index, field, value) => {
    setQuestionario((prev) => ({
      ...prev,
      questions: prev.questions.map((q, qIndex) => {
        if (qIndex !== qId) return q;
        
        return {
          ...q,
          options: q.options.map((opt, oIndex) => {
            if (oIndex !== index) return opt;
            
            // Se a opção é string (formato antigo), converte para objeto
            if (typeof opt === 'string') {
              return field === 'text' 
                ? { text: value, skipTo: null }
                : { text: opt, skipTo: value };
            }
            
            // Se já é objeto, atualiza o campo
            return { ...opt, [field]: value };
          }),
        };
      }),
    }));
  };

  const handleRequiredChange = (qId, value) => {
    setQuestionario((prev) => ({
      ...prev,
      questions: prev.questions.map((q, index) =>
        index === qId ? { ...q, required: value } : q
      ),
    }));
  };

  const handleTypeChange = (qIndex, value) => {
    setQuestionario((prev) => ({
      ...prev,
      questions: prev.questions.map((q, index) => {
        if (index !== qIndex) return q;
        if (value === 'text') {
          const { matrixRows: _m, ...rest } = q;
          return { ...rest, type: 'text', options: [] };
        }
        if (value === 'radio') {
          const { matrixRows: _m, ...rest } = q;
          return { ...rest, type: 'radio', options: [{ text: '', skipTo: null }] };
        }
        if (value === 'checkbox') {
          const { matrixRows: _m, ...rest } = q;
          return { ...rest, type: 'checkbox', options: [{ text: '', skipTo: null }] };
        }
        if (value === 'matrix') {
          return {
            ...q,
            type: 'matrix',
            matrixRows: ['', ''],
            options: [
              { text: '', skipTo: null },
              { text: '', skipTo: null },
            ],
          };
        }
        return q;
      }),
    }));
  };

  const handleMatrixRowChange = (qIndex, rowIndex, value) => {
    setQuestionario((prev) => ({
      ...prev,
      questions: prev.questions.map((q, idx) => {
        if (idx !== qIndex) return q;
        const rows = [...(q.matrixRows || [])];
        rows[rowIndex] = value;
        return { ...q, matrixRows: rows };
      }),
    }));
  };

  const addMatrixRow = (qIndex) => {
    setQuestionario((prev) => ({
      ...prev,
      questions: prev.questions.map((q, idx) =>
        idx === qIndex ? { ...q, matrixRows: [...(q.matrixRows || []), ''] } : q
      ),
    }));
  };

  const removeMatrixRow = (qIndex, rowIndex) => {
    setQuestionario((prev) => ({
      ...prev,
      questions: prev.questions.map((q, idx) => {
        if (idx !== qIndex) return q;
        const rows = (q.matrixRows || []).filter((_, i) => i !== rowIndex);
        return { ...q, matrixRows: rows.length ? rows : [''] };
      }),
    }));
  };

  const addQuestion = () => {
    setQuestionario((prev) => ({
      ...prev,
      questions: [...prev.questions, { tituloBloco: '', question: '', type: 'text', options: [{ text: '', skipTo: null }], required: false }],
    }));
  };

  const addQuestionAfter = (qIndex) => {
    setQuestionario((prev) => {
      const newQuestions = [...prev.questions];
      newQuestions.splice(qIndex + 1, 0, { tituloBloco: '', question: '', type: 'text', options: [{ text: '', skipTo: null }], required: false });
      return {
        ...prev,
        questions: newQuestions
      };
    });
  };

  const moveQuestionUp = (qIndex) => {
    if (qIndex === 0) return;
    setQuestionario((prev) => {
      const newQuestions = [...prev.questions];
      [newQuestions[qIndex - 1], newQuestions[qIndex]] = [newQuestions[qIndex], newQuestions[qIndex - 1]];
      return {
        ...prev,
        questions: newQuestions
      };
    });
  };

  const moveQuestionDown = (qIndex) => {
    if (qIndex === questionario.questions.length - 1) return;
    setQuestionario((prev) => {
      const newQuestions = [...prev.questions];
      [newQuestions[qIndex], newQuestions[qIndex + 1]] = [newQuestions[qIndex + 1], newQuestions[qIndex]];
      return {
        ...prev,
        questions: newQuestions
      };
    });
  };

  const removeQuestion = (qIndex) => {
    if (window.confirm('⚠️ Tem certeza que deseja remover esta pergunta? Esta ação não pode ser desfeita.')) {
      setQuestionario((prev) => ({
        ...prev,
        questions: prev.questions.filter((_, index) => index !== qIndex),
      }));
    }
  };

  const addOption = (qIndex) => {
    setQuestionario((prev) => ({
      ...prev,
      questions: prev.questions.map((q, index) =>
        index === qIndex ? { ...q, options: [...(q.options || []), { text: '', skipTo: null }] } : q
      ),
    }));
  };

  const removeOption = (qIndex, oIndex) => {
    if (window.confirm('🗑️ Deseja remover esta opção?')) {
      setQuestionario((prev) => ({
        ...prev,
        questions: prev.questions.map((q, index) =>
          index === qIndex
            ? { ...q, options: (q.options || []).filter((_, optIndex) => optIndex !== oIndex) }
            : q
        ),
      }));
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    
    if (window.confirm('💾 Deseja salvar todas as alterações feitas no questionário?')) {
      try {
        await updateDoc(
          doc(db, 'questionarios', id),
          omitUndefinedDeep({ ...questionario, gravarAudio: true })
        );
        alert('✅ Questionário atualizado com sucesso!');
        navigate('/');
      } catch (error) {
        console.error('Erro ao atualizar documento: ', error);
        alert('❌ Erro ao salvar questionário. Tente novamente.');
      }
    }
  };

  const duplicateQuestion = (id) => {
    const questionToDuplicate = questionario.questions.find(q => q.id === id);
    if (questionToDuplicate) {
      const newId = questionario.questions[questionario.questions.length - 1].id + 1;
      const duplicatedQuestion = { ...questionToDuplicate, id: newId };
      setQuestionario((prevQuestionario) => ({
        ...prevQuestionario,
        questions: [...prevQuestionario.questions, duplicatedQuestion],
      }));
      alert('✅ Pergunta duplicada com sucesso!');
    }
  };

  if (!questionario) {
    return <p>Carregando questionário...</p>;
  }

  return (
    <div className="container">
      <h1>✏️ Editar Questionário</h1>
      <form onSubmit={handleSave}>
        <div className="form-section">
          <label>
            📝 Nome do Questionário:
            <input
              type="text"
              name="nome"
              value={questionario.nome}
              onChange={handleChange}
              required
              placeholder="Digite o nome do questionário"
            />
          </label>
        </div>

        <div className="questions-container">
          {questionario.questions.map((q, qIndex) => (
            <div key={qIndex} className="question-card">
              <div className="question-header">
                <h3>Pergunta {qIndex + 1}</h3>
                <span className="required-badge">
                  {q.required ? '⭐ Obrigatória' : '📌 Opcional'}
                </span>
              </div>

              <div className="form-group">
                <label>Título do bloco (opcional):</label>
                <input
                  type="text"
                  value={q.tituloBloco ?? ''}
                  onChange={(e) => handleQuestionChange(qIndex, 'tituloBloco', e.target.value)}
                  placeholder="Ex.: Dados pessoais — deixe vazio para continuar no mesmo bloco"
                />
              </div>

              <div className="form-group">
                <label>Pergunta:</label>
                <input
                  type="text"
                  value={q.question}
                  onChange={(e) => handleQuestionChange(qIndex, 'question', e.target.value)}
                  required={q.required}
                  placeholder="Digite sua pergunta aqui"
                />
              </div>

              <div className="form-group">
                <label>Tipo de Resposta:</label>
                <select
                  value={q.type}
                  onChange={(e) => handleTypeChange(qIndex, e.target.value)}
                >
                  <option value="text">📝 Texto</option>
                  <option value="radio">⚪ Escolha única (uma opção)</option>
                  <option value="checkbox">☑ Várias opções (caixas de seleção)</option>
                  <option value="matrix">▦ Matriz (linhas e colunas)</option>
                </select>
              </div>

              {q.type === 'text' ? (
                <div className="form-group">
                  <input 
                    type="text" 
                    disabled 
                    placeholder="Resposta em texto livre" 
                    className="preview-input"
                  />
                </div>
              ) : q.type === 'matrix' ? (
                <>
                  <div className="options-section matrix-rows-section">
                    <label>Linhas (uma célula de texto por linha):</label>
                    {(q.matrixRows && q.matrixRows.length ? q.matrixRows : ['']).map((rowLabel, rowIndex) => (
                      <div key={rowIndex} className="option-input-row matrix-row-editor">
                        <span className="option-number">{rowIndex + 1}</span>
                        <input
                          type="text"
                          value={rowLabel}
                          onChange={(e) => handleMatrixRowChange(qIndex, rowIndex, e.target.value)}
                          placeholder={`Linha ${rowIndex + 1}`}
                          className="option-text-input"
                        />
                        <button
                          type="button"
                          className="btn-remove-option"
                          onClick={() => removeMatrixRow(qIndex, rowIndex)}
                          disabled={(q.matrixRows || ['']).length <= 1}
                          title="Remover linha"
                        >
                          🗑️
                        </button>
                      </div>
                    ))}
                    <button type="button" className="btn-add-option" onClick={() => addMatrixRow(qIndex)}>
                      ➕ Adicionar linha
                    </button>
                  </div>
                  <div className="options-section">
                    <label>Colunas (cabeçalhos — uma opção por coluna):</label>
                    {(q.options || []).map((option, oIndex) => {
                      const optionText = typeof option === 'string' ? option : option.text || '';
                      return (
                        <div key={oIndex} className="option-row-container">
                          <div className="option-input-row">
                            <span className="option-number">{oIndex + 1}</span>
                            <input
                              type="text"
                              value={optionText}
                              onChange={(e) => handleOptionChange(qIndex, oIndex, 'text', e.target.value)}
                              placeholder={`Coluna ${oIndex + 1}`}
                              className="option-text-input"
                            />
                            <button
                              type="button"
                              className="btn-remove-option"
                              onClick={() => removeOption(qIndex, oIndex)}
                              disabled={(q.options || []).length <= 1}
                              title="Remover coluna"
                            >
                              🗑️
                            </button>
                          </div>
                        </div>
                      );
                    })}
                    <button type="button" className="btn-add-option" onClick={() => addOption(qIndex)}>
                      ➕ Adicionar coluna
                    </button>
                  </div>
                </>
              ) : (
                <div className="options-section">
                  <label>Opções de Resposta:</label>
                  {(q.options || []).map((option, oIndex) => {
                    // Garante compatibilidade com formato antigo (string) e novo (objeto)
                    const optionText = typeof option === 'string' ? option : option.text || '';
                    const skipTo = typeof option === 'string' ? null : option.skipTo;
                    
                    return (
                      <div key={oIndex} className="option-row-container">
                        <div className="option-input-row">
                          <span className="option-number">{oIndex + 1}</span>
                          <input
                            type="text"
                            value={optionText}
                            onChange={(e) => handleOptionChange(qIndex, oIndex, 'text', e.target.value)}
                            placeholder={`Opção ${oIndex + 1}`}
                            className="option-text-input"
                          />
                          <button 
                            type="button" 
                            className="btn-remove-option" 
                            onClick={() => removeOption(qIndex, oIndex)}
                            title="Remover opção"
                          >
                            🗑️
                          </button>
                        </div>
                        {q.type === 'radio' && (
                        <div className="skip-logic-row">
                          <label className="skip-label">
                            🔀 Se escolher esta opção, pular para:
                          </label>
                          <select
                            value={skipTo === 'end' ? 'end' : skipTo != null && skipTo !== '' ? String(skipTo) : ''}
                            onChange={(e) =>
                              handleOptionChange(
                                qIndex,
                                oIndex,
                                'skipTo',
                                e.target.value === '' ? null : e.target.value === 'end' ? 'end' : parseInt(e.target.value, 10)
                              )
                            }
                            className="skip-select"
                          >
                            <option value="">➡️ Próxima pergunta (padrão)</option>
                            {questionario.questions.map((_, idx) => {
                              if (idx > qIndex) {
                                return (
                                  <option key={idx} value={idx}>
                                    ⏩ Pergunta {idx + 1}
                                  </option>
                                );
                              }
                              return null;
                            })}
                            <option value="end">✅ Finalizar questionário</option>
                          </select>
                        </div>
                        )}
                      </div>
                    );
                  })}
                  <button 
                    type="button" 
                    className="btn-add-option" 
                    onClick={() => addOption(qIndex)}
                  >
                    ➕ Adicionar Opção
                  </button>
                </div>
              )}

              <div className="form-group checkbox-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={q.required}
                    onChange={(e) => handleRequiredChange(qIndex, e.target.checked)}
                  />
                  <span>Marcar como pergunta obrigatória</span>
                </label>
              </div>

              <div className="question-actions">
                <div className="question-actions-row">
                  <button 
                    type="button" 
                    className="btn-move-up" 
                    onClick={() => moveQuestionUp(qIndex)}
                    disabled={qIndex === 0}
                    title="Mover para cima"
                  >
                    ⬆️
                  </button>
                  <button 
                    type="button" 
                    className="btn-move-down" 
                    onClick={() => moveQuestionDown(qIndex)}
                    disabled={qIndex === questionario.questions.length - 1}
                    title="Mover para baixo"
                  >
                    ⬇️
                  </button>
                  <button 
                    type="button" 
                    className="btn-add-after" 
                    onClick={() => addQuestionAfter(qIndex)}
                    title="Adicionar pergunta abaixo"
                  >
                    ➕ Adicionar Abaixo
                  </button>
                </div>
                <div className="question-actions-row">
                  <button 
                    type="button" 
                    className="btn-duplicate-question" 
                    onClick={() => duplicateQuestion(q.id)}
                  >
                    📋 Duplicar
                  </button>
                  <button 
                    type="button" 
                    className="btn-remove-question" 
                    onClick={() => removeQuestion(qIndex)}
                  >
                    🗑️ Remover
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="form-actions">
          <button 
            type="button" 
            className="btn-add-question" 
            onClick={addQuestion}
          >
            ➕ Adicionar Nova Pergunta
          </button>
          <button type="submit" className="btn-save">
            💾 Salvar Alterações
          </button>
          <button 
            type="button" 
            className="btn-back" 
            onClick={() => navigate('/')}
          >
            ⬅️ Voltar
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditarQuestionario;
